import { useRef, useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { MegaMenuConfig } from "./types";
import MenuPanel from "./menu-panel";
import { trackNavClick } from "@/utils/analytics";

interface DesktopMenuProps {
  menu: MegaMenuConfig;
  isActive?: boolean;
}

export default function DesktopMenu({
  menu,
  isActive = false,
}: DesktopMenuProps) {
  const router = useRouter();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  // Sticky: once a panel has been opened, keep media mounted for that session.
  const [loadMedia, setLoadMedia] = useState(false);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsOpen(true);
    setLoadMedia(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 200); // 200ms delay
  };

  // Close menu on route change
  useEffect(() => {
    const handleRouteChange = () => {
      setIsOpen(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    router.events.on("routeChangeStart", handleRouteChange);

    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [router.events]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (
        isOpen &&
        containerRef.current &&
        !containerRef.current.contains(e.target as Node) &&
        panelRef.current &&
        !panelRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isOpen]);

  return (
    <>
      <div
        ref={containerRef}
        className="flex group relative"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="relative flex flex-1">
          <Link
            href={menu.url}
            className="group relative z-10 flex items-center justify-center text-sm font-medium text-gray-700 transition-colors duration-200 ease-out hover:text-gray-800 shrink-0"
            onClick={() => {
              trackNavClick({
                navItem: menu.label,
                navUrl: menu.url,
              });
            }}
            onFocus={handleMouseEnter}
            onBlur={(e) => {
              // Only close if focus is moving outside the menu
              if (!containerRef.current?.contains(e.relatedTarget as Node)) {
                setIsOpen(false);
              }
            }}
            aria-expanded={isOpen}
            aria-controls={`menu-${menu.id}`}
          >
            <span className="flex items-center gap-2">
              {menu.label}
              {menu.badge && (
                <span className="rounded-full bg-brand-600 px-2 py-0.5 text-xs font-semibold text-white">
                  {menu.badge}
                </span>
              )}
            </span>
            <span
              aria-hidden="true"
              className={`absolute inset-x-0 bottom-0 h-0.5 transition-colors duration-200 ease-out sm:mt-5 sm:translate-y-px sm:transform ${
                isOpen ? "bg-brand-600" : "bg-transparent"
              }`}
            />
          </Link>
        </div>
      </div>

      <div
        ref={panelRef}
        id={`menu-${menu.id}`}
        role="region"
        aria-label={`${menu.label} menu`}
        className={`absolute left-0 w-full top-full z-50 transition-all duration-200 ease-out overflow-hidden border border-gray-200 rounded-xl shadow-lg ${
          isOpen
            ? "opacity-100 visible pointer-events-auto"
            : "opacity-0 invisible pointer-events-none"
        }`}
        style={{
          maxHeight: `calc(100vh - 64px)`,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        aria-hidden={!isOpen}
      >
        {/* Presentational element for shadow */}
        <div
          aria-hidden="true"
          className="absolute inset-0 top-1/2 bg-white shadow-lg"
        />

        <div
          className="relative bg-white"
          style={{ maxHeight: `calc(100vh - 64px)` }}
        >
          {/*
            Always SSR links/text (CSS-hidden when closed) for internal linking
            and SEO. Featured-card images load only after first open/hover.
          */}
          <MenuPanel menu={menu} loadMedia={loadMedia} />
        </div>
      </div>
    </>
  );
}
